import { useEffect, useState } from 'react';
import { Result, Button, Typography } from 'antd';
import { ClockCircleOutlined } from '@ant-design/icons';
import { LoadingSpinner } from './LoadingSpinner';
import { trackEvent } from '@/services/analytics';

const REDIRECT_TIMEOUT_MS = 5000;

interface ExternalRedirectProps {
    to: string;
}

/**
 * Redirects to an external URL (outside of the React app).
 * Shows a loading spinner while redirecting.
 * If redirect doesn't complete within 5 seconds, displays a fallback UI
 * with manual options for the user.
 */
export const ExternalRedirect = ({ to }: ExternalRedirectProps) => {
    const [hasTimedOut, setHasTimedOut] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => {
            // Track timeout event before showing fallback UI
            trackEvent('redirect_timeout', {
                destination_url: to,
                route_path: window.location.pathname,
                timeout_ms: REDIRECT_TIMEOUT_MS,
            });
            setHasTimedOut(true);
        }, REDIRECT_TIMEOUT_MS);

        // Attempt the redirect
        window.location.href = to;

        // Cleanup timer on unmount
        return () => clearTimeout(timer);
    }, [to]);

    if (hasTimedOut) {
        return (
            <Result
                icon={<ClockCircleOutlined style={{ color: 'var(--orange-500)' }} />}
                title="Taking longer than expected..."
                subTitle={
                    <>
                        We're trying to redirect you to:
                        <br />
                        <Typography.Text code style={{ wordBreak: 'break-all' }}>
                            {to}
                        </Typography.Text>
                    </>
                }
                extra={[
                    <Button key="back" onClick={() => window.history.back()}>
                        Go Back
                    </Button>,
                    <Button
                        key="open"
                        type="primary"
                        onClick={() => (window.location.href = to)}
                    >
                        Open Link
                    </Button>,
                    <Button
                        key="newTab"
                        type="link"
                        onClick={() => window.open(to, '_blank')}
                    >
                        Open in New Tab
                    </Button>,
                ]}
            />
        );
    }

    return <LoadingSpinner />;
};
